import { danger, notification } from 'redux-store/reducers/common-reducer';

import { createReducer } from 'utils/redux';
import { API, get, put } from 'utils/api';
import { __t, applyTranslations } from 'utils/language';
import { getAccessParams } from 'utils/shared-access';
import { logError } from 'utils/log';
import { PROPOSAL_SORT_TO_QUERY_PARAM, mapListAPIResponseEntry } from 'modules/proposal/utils/creator-proposal-utils';

import type {
  Action,
  PaginationLinks,
  PaginationMeta,
  ProposalListMember,
  ProposalPageFilterOption,
  ProposalPageSortOption,
  ThunkAction
} from 'types';
import { Querystring } from 'utils/url';

applyTranslations(require('./proposal-reducer-errors.i18n.json'));

const SET_LIST = 'sharedAccessList/SET_LIST';
const SET_MEMBER = 'sharedAccessList/SET_MEMBER';
const SET_LIST_LOADING_STATUS = 'sharedAccessList/SET_LIST_LOADING_STATUS';
const RESET_LIST = 'sharedAccessList/RESET_LIST';

type State = {
  loadingStatus: boolean,
  list: Array<ProposalListMember>,
  paginationLinks?: PaginationLinks,
  paginationMeta?: PaginationMeta
};

const initialState: State = {
  loadingStatus: false,
  list: [],
  paginationLinks: undefined,
  paginationMeta: undefined
};

function setListLoadingStatus(loadingStatus: boolean) {
  return {
    type: SET_LIST_LOADING_STATUS,
    payload: {
      loadingStatus
    }
  };
}

export function resetList(): Action {
  return {
    type: RESET_LIST
  };
}

function listLoaded(list: Array<any>, links: PaginationLinks, meta: PaginationMeta) {
  return {
    type: SET_LIST,
    payload: {
      list,
      links,
      meta
    }
  };
}

function setListMember(memberData: ProposalListMember) {
  return {
    type: SET_MEMBER,
    payload: {
      memberData
    }
  };
}

export function updateCreator(data: ProposalListMember, isApprovalStatusReset?: boolean): ThunkAction {
  return async (dispatch, getState) => {
    try {
      const mappedData = {
        ...data,
        instagramImages: data.images,
        images: undefined,
        isApprovalStatusReset: Boolean(isApprovalStatusReset)
      };

      const { config, options } = getAccessParams(getState);
      const url = API.END_POINTS.CREATOR_APPROVAL.creatorListMember(data.creatorApprovalId, data.id);
      await put(url, {
        ...config,
        data: mappedData
      }, options);
      dispatch(setListMember(data));
    } catch (error) {
      dispatch(danger(__t('proposal_reducer_errors.list_update_failed')));
      throw error;
    }
  };
}

export function updateApprovedStatus(data: ProposalListMember): ThunkAction {
  return async (dispatch, getState) => {
    try {
      const { config, options } = getAccessParams(getState);
      const url = API.END_POINTS.CREATOR_APPROVAL.setListMemberApproval(data.creatorApprovalId, data.id);
      await put(url, {
        ...config,
        data: {
          approvalStatus: data.approvalStatus
        }
      }, options);
      dispatch(setListMember(data));
    } catch (error) {
      dispatch(danger(__t('proposal_reducer_errors.list_update_failed')));
      logError(error);
    }
  };
}

export function loadCreatorList(
  page: number,
  filter: ProposalPageFilterOption,
  sort: ProposalPageSortOption
): ThunkAction {
  return async (dispatch, getState) => {
    dispatch(setListLoadingStatus(true));
    try {
      const { config, options } = getAccessParams(getState);
      const query = {
        page,
        filter: (filter !== 'all') ? filter : undefined,
        sort: PROPOSAL_SORT_TO_QUERY_PARAM[sort]
      };

      const url = API.END_POINTS.CREATOR_APPROVAL.creatorList(Querystring.objectToString(query));
      const response = await get(url, config, options);
      const {
        data,
        links,
        meta
      } = response.data;
      dispatch(listLoaded(data.map(mapListAPIResponseEntry), links, meta));
    } catch (error) {
      dispatch(danger(__t('proposal_reducer_errors.list_fetch_failed')));
      if (error.response && error.response.status) {
        dispatch(notification({
          status: error.response.status
        }));
      }
      throw error;
    } finally {
      dispatch(setListLoadingStatus(false));
    }
  };
}

export default createReducer<State, any>('proposalApprovePage', initialState, {
  [RESET_LIST]: () => initialState,
  [SET_LIST_LOADING_STATUS]: (state, { loadingStatus }) => ({
    ...state,
    loadingStatus
  }),
  [SET_LIST]: (state, { list, links, meta }) => ({
    ...state,
    list,
    paginationLinks: links,
    paginationMeta: meta
  }),
  [SET_MEMBER]: (state, { memberData }) => ({
    ...state,
    list: state.list.map((entry) => {
      if (entry.id === memberData.id) {
        return memberData;
      }

      return entry;
    })
  })
});
